import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { X, ArrowLeft } from "lucide-react";
import { api1 } from "../../utils/axiosInstance";
import { initState, emptyCat } from "./constants";
import { useSkillsGapData } from "./hooks/useSkillsGapData";
import { useSkillManagement } from "./hooks/useSkillManagement";
import SelectionView from "./SelectionView";
import ComparisonView from "./ComparisonView";

const SkillsGapAnalysisToolModal = ({ isOpen, onClose, onAnalyzeGap }) => {
  const { currentJob, targetJob, occupationId } = useSelector((state) => state.user);
  const userId = useSelector((state) => state.auth.id);
  const targetOccupationFromRedux = useSelector((state) => state.user.targetJob);

  const [state, setState] = useState(initState);
  const [selectedSkills, setSelectedSkills] = useState(new Set());

  const isInitialLoad = useRef(true);
  const { register, watch, setValue, formState: { errors } } = useForm({ defaultValues: { targetOccupation: "" } });
  const targetOcc = watch("targetOccupation");

  const setPartial = (obj) => setState((prevState) => ({ ...prevState, ...obj }));

  // Use custom hooks
  const { skillsCategories, setSkillsCategories, fetchTargetOccupationSkills, loadTargetCareers } = useSkillsGapData(occupationId, currentJob);
  const { isSkillMatching, toggleSkill, formatSkills, getSkillsCount } = useSkillManagement(selectedSkills, setSelectedSkills);

  const saveSkillGapAnalysis = useCallback(async () => {
    if (!userId || !state.targetOccupation || !state.skillsData) return;
    setPartial({ isSaving: true });
    try {
      const matching = [], gap = [];
      Object.values(state.skillsData).flat().forEach(skill => isSkillMatching(skill) ? matching.push(skill) : gap.push(skill));
      await api1.post("/api/skill-gap-analysis", {
        targetOccupation: state.targetOccupation,
        matchingSkills: matching,
        skillsGap: gap
      });
    } catch (e) {
      console.error?.("Save failed:", e);
    } finally {
      setPartial({ isSaving: false });
    }
  }, [userId, state.targetOccupation, state.skillsData, isSkillMatching]);

  const handleAnalyzeGap = () => {
    const targetToUse = targetOcc || state.targetOccupation || targetOccupationFromRedux || targetJob;
    const career = state.targetOccupationOptions.find(c => c.name === targetToUse);
    const skillsDataForFormat = {
      commonSkills: skillsCategories.commonSkills?.skills || [],
      definingSkills: skillsCategories.definingSkills?.skills || [],
      distinguishingSkills: skillsCategories.distinguishingSkills?.skills || [],
      salaryBoostingSkills: skillsCategories.salaryBoostingSkills?.skills || [],
    };
    setPartial({
      currentView: "comparison",
      skillsData: formatSkills(skillsDataForFormat),
      targetOccupation: targetToUse
    });
    onAnalyzeGap?.({
      baseOccupation: currentJob,
      targetOccupation: targetToUse,
      selectedSkills: [...selectedSkills],
      targetOccupationType: career?.type
    });
  };

  const handleBackToSelection = () => {
    setPartial({ currentView: "selection" });
    const targetToUse = targetOcc || state.targetOccupation || targetOccupationFromRedux || targetJob;
    if (targetToUse) {
      setValue("targetOccupation", targetToUse);
      setTimeout(() => {
        setValue("targetOccupation", targetToUse);
        setPartial({ lastBackNavigation: Date.now() });
      }, 100);
    }
  };

  const handleClose = () => {
    // Clear all state when modal closes
    setState(initState);
    setSelectedSkills(new Set());
    setSkillsCategories(emptyCat());
    isInitialLoad.current = true;
    onClose();
  };

  // keep form target sync
  useEffect(() => setPartial({ targetOccupation: targetOcc || "" }), [targetOcc]);

  useEffect(() => {
    if (!isOpen) return;
    const targetFromRedux = targetOccupationFromRedux || targetJob || "";
    setPartial({ targetOccupation: targetFromRedux });
    setValue("targetOccupation", targetFromRedux);
    
    const loadCareers = async () => {
      setPartial({ isLoadingTargetCareers: !isInitialLoad.current, isInitialCareerFetch: isInitialLoad.current });
      const result = await loadTargetCareers();
      if (result.success) {
        setPartial({ 
          targetOccupationOptions: result.data, 
          isLoadingTargetCareers: false, 
          isInitialCareerFetch: false 
        });
      } else {
        setPartial({ isLoadingTargetCareers: false, isInitialCareerFetch: false });
      }
      isInitialLoad.current = false;
    };
    
    loadCareers();
  }, [isOpen, targetOccupationFromRedux, targetJob, loadTargetCareers, setValue]);

  // set form value when options loaded
  useEffect(() => {
    if (!isOpen || !state.targetOccupationOptions.length) return;
    const targetFromRedux = targetOccupationFromRedux || targetJob;
    if (targetFromRedux) {
      setValue("targetOccupation", targetFromRedux);
      setPartial({ targetOccupation: targetFromRedux });
    }
  }, [isOpen, state.targetOccupationOptions, targetOccupationFromRedux, targetJob, setValue]);

  // fetch skills whenever target occupation is available
  useEffect(() => {
    if (!isOpen || !state.targetOccupationOptions.length) return;
    const targetToUse = targetOcc || state.targetOccupation || targetOccupationFromRedux || targetJob;
    if (!targetToUse) return;
    const career = state.targetOccupationOptions.find(c => c.name === targetToUse);
    if (!career) return;
    
    setPartial({
      isLoadingSkills: true,
      skillsError: null,
      skillsLoaded: false,
      skillsData: null
    });
    setSelectedSkills(new Set());
    
    const fetchSkills = async () => {
      const result = await fetchTargetOccupationSkills(targetToUse, career.type);
      if (result.success) {
        setPartial({ skillsLoaded: true, skillsError: null });
      } else {
        setPartial({ skillsError: result.error, skillsLoaded: true });
      }
      setPartial({ isLoadingSkills: false });
    };
    
    fetchSkills();
  }, [isOpen, state.targetOccupationOptions, targetOccupationFromRedux, targetJob, targetOcc, fetchTargetOccupationSkills]);

  // Save when in comparison view and skills loaded
  useEffect(() => {
    if (state.currentView === "comparison" && state.skillsLoaded && state.skillsData) saveSkillGapAnalysis();
  }, [state.currentView, state.skillsLoaded, state.skillsData, saveSkillGapAnalysis]);

  // Cleanup effect when modal closes
  useEffect(() => {
    if (!isOpen) {
      // Reset all state when modal is closed
      setState(initState);
      setSelectedSkills(new Set());
      setSkillsCategories(emptyCat());
      isInitialLoad.current = true;
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-[9999] p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] sm:max-h-[80vh] overflow-y-auto relative">
        <div className="flex items-center justify-between p-3 sm:p-4 border-b">
          <div className="flex items-center gap-3 flex-1">
            {state.currentView === "comparison" && (
              <button onClick={handleBackToSelection} className="text-gray-500 hover:text-gray-700">
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <div>
              <h2 className="text-lg font-bold">
                {state.currentView === "selection" ? "Skills Gap Analysis Tool" : "Skills Gap Analysis Results"}
              </h2>
              <p className="text-sm text-gray-600">
                {state.currentView === "selection" ? "Compare skills between current and target jobs" : "Compare your current and target skills"}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
        </div>
        {state.currentView === "selection" ? (
          <SelectionView
            state={state}
            setPartial={setPartial}
            targetOcc={targetOcc}
            targetOccupationFromRedux={targetOccupationFromRedux}
            targetJob={targetJob}
            currentJob={currentJob}
            skillsCategories={skillsCategories}
            selectedSkills={selectedSkills}
            toggleSkill={toggleSkill}
            handleAnalyzeGap={handleAnalyzeGap}
            fetchTargetOccupationSkills={fetchTargetOccupationSkills}
            register={register}
            errors={errors}
          />
        ) : (
          <ComparisonView
            state={state}
            setPartial={setPartial}
            targetOcc={targetOcc}
            targetOccupationFromRedux={targetOccupationFromRedux}
            targetJob={targetJob}
            isSkillMatching={isSkillMatching}
            getSkillsCount={getSkillsCount}
            fetchTargetOccupationSkills={fetchTargetOccupationSkills}
          />
        )}
      </div>
    </div>
  );
};

export default SkillsGapAnalysisToolModal;
